import type { SymbolRecord } from '../../types'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'
import { displaySymbol } from '../../utils/displaySymbol'
import { MIN_WIN_RATE_SAMPLE, formatWinRate, hasWinRate, isSmallSample } from '../../utils/winRate'
import Tooltip from '../Tooltip'
import { IntervalNote, formatR, pnlClass } from './recordUI'

/**
 * The engine's closed record by coin (GET /api/track-record/summary
 * closed_by_symbol: services/signal_summary_service.py _symbol_records), most
 * closes first. Display only: every figure is the backend's, computed per coin
 * with the headline's own formulas. Each row carries its own count; a rate is
 * withheld below MIN_WIN_RATE_SAMPLE and flagged as a small sample below
 * SMALL_SAMPLE_SIZE, with its interval beneath, so three wins from three
 * closes never reads as a 100% coin. Before fees, like every result shown.
 */
const AssetSplitPanel = ({ records }: { records: SymbolRecord[] }) => (
  <section data-testid="asset-split" aria-labelledby="asset-split-title" className="space-y-3">
    <div>
      <h2 id="asset-split-title" className="text-sm font-semibold text-gray-100">
        <Tooltip content={GLOSSARY.assetSplit}>By coin</Tooltip>
      </h2>
      <p className="text-xs text-gray-400">
        Closed signals grouped by coin. Same numbers, same formulas, before fees. Coins move together, so read each row
        with its count.
      </p>
    </div>

    <div className="overflow-x-auto rounded-lg border border-terminal-border bg-terminal-surface">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
            <th scope="col" className="px-3 py-2 font-medium">
              Coin
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Closed
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Win rate
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Avg R
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const quoted = hasWinRate(r.win_rate, r.closed)
            const testId = `asset-${r.symbol.replace('/', '-')}`
            return (
              <tr key={r.symbol} className="border-t border-terminal-border align-top" data-testid={testId}>
                <td className="px-3 py-2 font-medium text-gray-100 whitespace-nowrap">{displaySymbol(r.symbol)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-300 whitespace-nowrap">
                  {r.closed}
                  <span className="block text-[11px] text-gray-500">
                    {r.wins}W/{r.losses}L{r.breakevens > 0 && `/${r.breakevens}BE`}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {quoted ? (
                    <>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-mono text-sm tabular-nums text-gray-100" data-testid={`${testId}-win-rate`}>
                          {formatWinRate(r.win_rate, r.closed)}
                        </span>
                        {isSmallSample(r.closed) && (
                          <Tooltip content={GLOSSARY.smallSample}>
                            <span className="whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 bg-amber-500/15 text-amber-300 ring-amber-500/30">
                              small sample
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <IntervalNote
                        interval={r.win_rate_interval}
                        format={(value) => `${value.toFixed(1)}%`}
                        testId={`${testId}-win-rate-interval`}
                        dense
                      />
                    </>
                  ) : (
                    <span className="text-gray-500" data-testid={`${testId}-win-rate`}>
                      — · {MIN_WIN_RATE_SAMPLE - r.closed} more close{MIN_WIN_RATE_SAMPLE - r.closed === 1 ? '' : 's'}{' '}
                      before a rate
                    </span>
                  )}
                </td>
                <td
                  className={`px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap ${
                    r.avg_r_gross == null ? 'text-gray-500' : pnlClass(r.avg_r_gross)
                  }`}
                  data-testid={`${testId}-avg-r`}
                >
                  {r.avg_r_gross == null ? '—' : formatR(r.avg_r_gross)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  </section>
)

export default AssetSplitPanel
